import { User as UserIcon } from "lucide-react";
import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../../../context/AuthContext";
import { cn } from "../../../lib/utils";
import {
  card,
  surfaceMuted,
  pageTitle,
  sectionTitle,
  microLabel,
  bodySecondary,
  accentBarSection,
  accentBarCard,
  badgeBase,
  motionFadeUp,
  motionStaggerChild,
} from "../../../lib/design-system";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const val = (v) => (v ? v : "N/A");
const boolVal = (v) => (v === true ? "Yes" : v === false ? "No" : "N/A");

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const DataField = ({ label, value, index, sectionIndex }) => (
  <motion.div
    {...motionStaggerChild(index, sectionIndex * 0.15)}
    className={cn(
      surfaceMuted,
      "rounded-sm border border-gray-200 dark:border-gray-700 p-3"
    )}
  >
    <div className={microLabel}>{label}</div>
    <div className="text-gray-800 dark:text-gray-200 mt-1">
      {val(value)}
    </div>
  </motion.div>
);

const colsMap = {
  1: "grid-cols-1",
  2: "grid-cols-1 md:grid-cols-2",
  3: "grid-cols-1 md:grid-cols-3",
};

const Section = ({ title, columns = 3, index, children }) => (
  <motion.div
    {...motionStaggerChild(index, 0.1)}
    className={cn(card, "p-6 space-y-4")}
  >
    <div>
      <h2 className={sectionTitle}>{title}</h2>
      <div className={cn(accentBarSection, "mt-2")} />
    </div>
    <div className={cn("grid gap-4", colsMap[columns])}>{children}</div>
  </motion.div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Skeleton loader
// ---------------------------------------------------------------------------

const SkeletonBlock = ({ className }) => (
  <div className={cn("animate-pulse bg-gray-200 dark:bg-gray-700 rounded-sm", className)} />
);

const ProfileSkeleton = () => (
  <div className="py-6">
    <div className="max-w-6xl mx-auto px-4 space-y-6">
      {/* Header skeleton */}
      <div className={cn(card, "overflow-hidden")}>
        <SkeletonBlock className="w-full h-1.5" />
        <div className="p-6 flex flex-col sm:flex-row items-center gap-6">
          <SkeletonBlock className="w-28 h-28 sm:w-32 sm:h-32" />
          <div className="flex-grow space-y-3 w-full">
            <SkeletonBlock className="h-8 w-48" />
            <div className="flex gap-3">
              <SkeletonBlock className="h-6 w-20" />
              <SkeletonBlock className="h-6 w-32" />
            </div>
          </div>
        </div>
      </div>

      {/* Section skeletons */}
      {[3, 3, 2, 2, 1].map((cols, i) => (
        <div key={i} className={cn(card, "p-6 space-y-4")}>
          <SkeletonBlock className="h-5 w-40" />
          <SkeletonBlock className="h-1 w-12" />
          <div className={cn("grid gap-4", cols === 1 ? "grid-cols-1" : cols === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-3")}>
            {Array.from({ length: cols }).map((_, j) => (
              <div key={j} className={cn(surfaceMuted, "rounded-sm border border-gray-200 dark:border-gray-700 p-3 space-y-2")}>
                <SkeletonBlock className="h-3 w-20" />
                <SkeletonBlock className="h-5 w-full" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const StudentProfilePage = () => {
  const { user } = useAuth();

  const fields = useMemo(
    () => ({
      name: val(user?.name),
      email: val(user?.email),
      mobile: val(user?.mobileNo),
      gender: val(user?.gender),
      age: val(user?.ageAsOn2025),
      nationality: val(user?.nationality),
      motherTongue: val(user?.motherTongue),
      religion: val(user?.religion),
      category: val(user?.category),
      physicallyChallenged: boolVal(user?.areYouPhysicallyChallenged),
      bloodGroup: val(user?.bloodGroup),
      aadhaar: val(user?.aadhaarNumber),
      passport: val(user?.passportNumber),
      permanentAddress: val(user?.fullPermanentAddress),
      correspondenceAddress: val(user?.fullCorrespondenceAddress),
      sameAddress: boolVal(
        user?.isYourCorrespondenceAddressSameAsPermanentAddress
      ),
      guardian: val(user?.parentGuardianDetails),
      role: val(user?.role),
      id: val(user?.id || user?._id),
    }),
    [user]
  );

  if (!user) return <ProfileSkeleton />;

  return (
    <motion.div
      {...motionFadeUp}
      className="py-6"
    >
      <div className="max-w-6xl mx-auto px-4 space-y-6">
        {/* Profile Header */}
        <motion.div
          {...motionStaggerChild(0)}
          className={cn(card, "overflow-hidden")}
        >
          <div className={cn(accentBarCard, "w-full h-1.5")} />
          <div className="p-6">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-sm overflow-hidden flex items-center justify-center bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-700">
                <UserIcon className="w-16 h-16 text-gray-400" />
              </div>
              <div className="flex-grow text-center sm:text-left">
                <h1 className={pageTitle}>{fields.name}</h1>
                <div className="mt-3 flex flex-wrap justify-center sm:justify-start gap-3 items-center">
                  <span
                    className={cn(
                      badgeBase,
                      "text-accent1 bg-accent1/10 border-accent1/30 capitalize"
                    )}
                  >
                    {fields.role}
                  </span>
                  <span className={bodySecondary}>ID: {fields.id}</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Basic Information */}
        <Section title="Basic Information" columns={3} index={1}>
          <DataField label="Name" value={fields.name} index={0} sectionIndex={1} />
          <DataField label="Email" value={fields.email} index={1} sectionIndex={1} />
          <DataField label="Mobile" value={fields.mobile} index={2} sectionIndex={1} />
        </Section>

        {/* Personal Details */}
        <Section title="Personal Details" columns={3} index={2}>
          <DataField label="Gender" value={fields.gender} index={0} sectionIndex={2} />
          <DataField label="Age (as on 2025)" value={fields.age} index={1} sectionIndex={2} />
          <DataField label="Nationality" value={fields.nationality} index={2} sectionIndex={2} />
          <DataField label="Mother Tongue" value={fields.motherTongue} index={3} sectionIndex={2} />
          <DataField label="Religion" value={fields.religion} index={4} sectionIndex={2} />
          <DataField label="Category" value={fields.category} index={5} sectionIndex={2} />
          <DataField label="Physically Challenged" value={fields.physicallyChallenged} index={6} sectionIndex={2} />
          <DataField label="Blood Group" value={fields.bloodGroup} index={7} sectionIndex={2} />
        </Section>

        {/* Identification */}
        <Section title="Identification" columns={2} index={3}>
          <DataField label="Aadhaar Number" value={fields.aadhaar} index={0} sectionIndex={3} />
          <DataField label="Passport Number" value={fields.passport} index={1} sectionIndex={3} />
        </Section>

        {/* Addresses */}
        <Section title="Addresses" columns={2} index={4}>
          <DataField label="Permanent Address" value={fields.permanentAddress} index={0} sectionIndex={4} />
          <DataField label="Correspondence Address" value={fields.correspondenceAddress} index={1} sectionIndex={4} />
          <DataField label="Is Correspondence same as Permanent?" value={fields.sameAddress} index={2} sectionIndex={4} />
        </Section>

        {/* Parent/Guardian */}
        <Section title="Parent/Guardian" columns={1} index={5}>
          <DataField label="Name" value={fields.guardian} index={0} sectionIndex={5} />
        </Section>
      </div>
    </motion.div>
  );
};

export default StudentProfilePage;
